// Explanatory popups in English. The Spanish version is in
// explicaciones.es.js and hud/explicaciones.js picks one by language. Each
// entry has a code (the HUD line on top), a title and the body in JSX.
//
// The English text uses the term of the paper, commuter-adjusted
// accessibility, and names the two anchors (home and workplace) so that the
// gold and red layers can be read without knowing the concept.

// First page of the briefing: the concept, the same in both versions.
const INTRO_CONCEPTO = (
  <>
    <p>
      Accessibility studies score access to everyday services from the place
      of residence. People spend a large part of the day at work, so the
      population that competes for services in a place includes the people who
      work there. Adding that population to the demand gives commuter-adjusted
      accessibility.
    </p>
    <p>
      The map covers Greater Santiago, Chile. Each hexagon is a cell of the
      city, coloured by its walking access to pharmacies, supermarkets, banks
      and health centres.
    </p>
  </>
);

// Second page: the controls. The mobile layout has no legend, no α selector,
// no ? buttons and no hover tooltip, so it gets its own version and App.js
// picks one by screen width.
const INTRO_CONTROLES = (
  <>
    <p>
      Click on a cell to launch a scan. The <strong>gold</strong> pulse covers
      what its residents reach on foot in 30 minutes. The{' '}
      <span className="modal-rojo">red</span> echoes do the same from the
      places where they work.
    </p>
    <p>
      The <strong>α</strong> buttons of the colour panel add workers to the
      demand. The <strong>?</strong> signs open the explanation of each panel.
    </p>
    <p>
      Hovering over a cell shows its summary without scanning. You can also
      move with the arrow keys: each keystroke selects a neighbouring cell and
      relaunches the scan.
    </p>
  </>
);

const INTRO_CONTROLES_MOVIL = (
  <>
    <p>
      Tap a cell to launch a scan. The <strong>gold</strong> pulse covers what
      its residents reach on foot in 30 minutes. The{' '}
      <span className="modal-rojo">red</span> echoes do the same from the
      places where they work.
    </p>
    <p>
      Hexagon colour shows accessibility at α = 0.5. Each worker counts as
      half a person in the demand of the place where they work.
    </p>
    <p>
      The bottom strip summarises the selected cell: its score, its type and
      the services it has lit so far. The LOICA box in the corner opens the
      credits and the method.
    </p>
  </>
);

export const EXPLICACIONES = {
  intro: {
    codigo: 'BRIEFING // 00',
    titulo: 'Commuter-adjusted accessibility',
    paginas: [INTRO_CONCEPTO, INTRO_CONTROLES],
    paginasMovil: [INTRO_CONCEPTO, INTRO_CONTROLES_MOVIL],
  },

  e2sfca: {
    codigo: 'INDEX // E2SFCA',
    titulo: 'The E2SFCA score',
    cuerpo: (
      <>
        <p>
          The E2SFCA index (enhanced two-step floating catchment area) sums up
          how well served a cell is, in a number between 0 and 1. It weighs
          the supply of services against the demand of the people who compete
          for them.
        </p>
        <p>
          The calculation has two steps. First, each service divides its
          capacity among all the population that reaches it on foot. Then each
          cell adds up the shares of the services within its reach.
        </p>
        <div className="modal-formula">
          reach by travel time: under 10 min weighs 1.00; 10 to 20 min weighs
          0.42; 20 to 30 min weighs 0.03
        </div>
        <p>
          Reach decays with travel time, so nearby services glow brighter than
          distant ones in the animation. The bars break the index down by
          service type. The total combines them, and it gives pharmacies and
          supermarkets twice the weight of banks and health centres.
        </p>
      </>
    ),
  },

  alpha: {
    codigo: 'PARAMETER // α',
    titulo: 'The α parameter',
    cuerpo: (
      <>
        <p>
          α controls how much workers weigh in the demand of each cell.
        </p>
        <div className="modal-formula">
          demand = residents + α times workers
        </div>
        <p>
          With <strong>α = 0</strong> only residents count, which is the usual
          residential accessibility. With <strong>α = 1</strong> each worker
          competes as much as a resident for the same services.
        </p>
        <p>
          As α grows, demand grows where employment concentrates and the score
          falls. In Greater Santiago the mean drops from 0.35 at α = 0 to 0.27
          at α = 1. That drop separates centres from employment districts.
        </p>
      </>
    ),
  },

  segundoOrden: {
    codigo: 'CONCEPT // COMMUTERS',
    titulo: 'The commuter adjustment',
    cuerpo: (
      <>
        <p>
          Accessibility <strong>from home</strong> measures what a person
          reaches from the place of residence. Accessibility{' '}
          <span className="modal-rojo">from the workplace</span> measures what
          the same person reaches during the working day.
        </p>
        <p>
          When you click on a cell, a home-work assignment model estimates
          where its employed residents work. The search for services on foot
          runs again from each of those destinations.
        </p>
        <p>
          The panel reports how many of those services are{' '}
          <span className="modal-rojo">new</span>: the ones a resident reaches
          from work but not from home. Residential accessibility leaves that
          set out.
        </p>
      </>
    ),
  },

  tipologia: {
    codigo: 'PANEL A // TYPOLOGY',
    titulo: 'Typology of the cell',
    cuerpo: (
      <>
        <p>
          The main panel of the figure crosses two quantities: the
          accessibility a cell <strong>retains</strong> when workers enter the
          demand (α = 0.5) and the <strong>weight</strong> of the working
          population, the share of the daytime population of the cell that
          arrives to work.
        </p>
        <p>That crossing gives four corners:</p>
        <ul>
          <li>
            <strong>Mixed-use centre</strong>: commuters arrive and the
            services hold up (the historic centre, the office axis of the
            north-east, the municipal subcentres).
          </li>
          <li>
            <strong>Monofunctional employment district</strong>: commuters
            arrive and there are no services (the industrial districts of the
            north and the west, the northern office park).
          </li>
          <li>
            <strong>Well-served residential area</strong>: few commuters and
            good access to services.
          </li>
          <li>
            <strong>Underserved area</strong>: few commuters and little access
            to services.
          </li>
        </ul>
        <p>
          The matrix highlights the corner of the selected cell. The{' '}
          <strong>typology</strong> button of the colour panel paints the whole
          map with those nine colours.
        </p>
      </>
    ),
  },

  escaneo: {
    codigo: 'SCAN // 30 MIN',
    titulo: 'The scan',
    cuerpo: (
      <>
        <p>
          The scan animates the catchment area of the cell. In a few seconds
          it covers the services that its residents reach on foot, up to 30
          minutes away.
        </p>
        <p>
          Travel times are real, computed with the R5 engine over the
          OpenStreetMap street network. For this reason the front does not
          advance in perfect circles: it follows the shape of the street
          layout.
        </p>
        <p>
          The <strong>gold</strong> pulse starts from the selected cell. The{' '}
          <span className="modal-rojo">red</span> echoes start from the
          workplaces. The dots mark each service reached, coloured by type.
        </p>
      </>
    ),
  },
};
